// src/pages/IdentityValidation.js
import React, { useEffect, useState, useCallback } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { HubConnectionBuilder } from "@microsoft/signalr";
import axios from "axios";
import incode from "../services/incode";
import Steps from "../components/Steps";
import FrontId from "../components/FrontId";
import BackId from "../components/BackId";
import ProcessId from "../components/ProcessId";
import Selfie from "../components/Selfie";
import FaceMatch from "../components/FaceMatch";
import RetrySteps from "../components/RetrySteps";
import LoadingSpinner from "../components/LoadingSpinner";
import ErrorComponent from "../components/ErrorComponent";
import '../components/styles/LoadingOverlay.css';

const apiUrl = process.env.REACT_APP_API_BASE_URL;

const IdentityValidation = () => {
    const [transactionId, setTransactionId] = useState(null);
    const [token, setToken] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isMobile, setIsMobile] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [session, setSession] = useState();
    const [step, setStep] = useState(0);
    const [error, setError] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [showQR, setShowQR] = useState(false);
    const [liveness, setLiveness] = useState(false);
    const [userExists, setUserExists] = useState(false);
    const [documentType, setDocumentType] = useState(); // Estado para el tipo de documento

    // Detectar si es móvil o PC
    useEffect(() => {
        const userAgent = navigator.userAgent || navigator.vendor || window.opera;
        const mobile = /android|iphone|ipad|ipod/i.test(userAgent);
        setIsMobile(mobile);
        setShowQR(!mobile);
        setLoading(false);
    }, []);

    // Obtener parámetros de la URL
    useEffect(() => {
        const queryParams = new URLSearchParams(window.location.search);
        setTransactionId(queryParams.get("transactionId"));
        setToken(queryParams.get("token"));
    }, []);

    // Configurar SignalR para sincronización
    const setupSignalRConnection = useCallback(async () => {
        const connection = new HubConnectionBuilder()
            .withUrl(`${apiUrl}/notificacionHub`, {
                withCredentials: true,
            })
            .withAutomaticReconnect()
            .build();

        connection.on("StartProcessing", () => {
            console.log("Evento StartProcessing recibido en el PC");
            setIsProcessing(true);
        });

        connection.on("ValidationCompleted", (receivedTransactionId, redirectUrl) => {
            if (receivedTransactionId === transactionId) {
                console.log("Evento ValidationCompleted recibido en el PC");
                setIsProcessing(false);
                if (redirectUrl) {
                    if (!/^https?:\/\//i.test(redirectUrl)) {
                        redirectUrl = `https://${redirectUrl}`;
                    }                     
                    window.location.href = redirectUrl;
                }
            }
        });

        try {
            await connection.start();
            console.log("Conexión SignalR establecida en el PC");
        } catch (error) {
            console.error("Error al conectar con SignalR:", error);
        }
    }, [transactionId]);

    // Configurar sesión para móvil
    useEffect(() => {
        const fetchTransactionStatus = async () => {
            try {
                setLoading(true);
                const response = await axios.get(`${apiUrl}/api/v1/TransactionValidation/${transactionId}`, {
                    headers: { Authorization: `Bearer ${token}` },
                });

                if (response.data.status === 0) {
                    setDocumentType(response.data.documentTypeId);

                    const session = await incode.createSession("ALL", null, {
                        configurationId: response.data.flowId,
                    });
                    await incode.warmup();
                    setSession(session);

                    await axios.post(`${apiUrl}/api/v1/Notificacion/start-validation`, transactionId, {
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${token}`,
                        },
                    });
                } else {
                    setError(true);
                    setErrorMessage("Estado de transacción no válido o caducado.");
                }
            } catch (err) {
                console.error("Error al obtener el estado de la transacción:", err);
                setError(true);
                setErrorMessage("Error al obtener el estado de la transacción. Por favor, intente de nuevo.");
            } finally {
                setLoading(false);
            }
        };

        if (isMobile && transactionId && token) {
            fetchTransactionStatus();
        } else if (!isMobile && transactionId && token) {
            setupSignalRConnection();
        }
    }, [isMobile, transactionId, token, setupSignalRConnection]);

    const goNext = () => setStep((prevStep) => prevStep + 1);

   //const handleError = (e) => {
   //     setErrorMessage(e.message || "Error durante el proceso de validación.");
   //     setError(true);
   // };

   const handleError = async (step, errorDetails) => {
    try {
        // Mostrar el error en la interfaz
        setErrorMessage(errorDetails.message || "Ha ocurrido un error en el proceso de validación.");
        setError(true);

        // Clonar el objeto errorDetails sin las imágenes
        const sanitizedErrorDetails = { ...errorDetails };

        if (sanitizedErrorDetails.details) {
            sanitizedErrorDetails.details = { ...sanitizedErrorDetails.details };

            // Remover las propiedades que contienen imágenes
            delete sanitizedErrorDetails.details.originalImage;
            delete sanitizedErrorDetails.details.frontIdImage;
            delete sanitizedErrorDetails.details.croppedIDFace;
        }

        // Enviar el error a la API
        const payload = {
            id: transactionId,
            transactionIncodeId: session?.interviewId,
            transactionIncodeToken: session?.token,
            step,
            errorDetails: JSON.stringify(sanitizedErrorDetails),
        };

        const response = await axios.put(`${apiUrl}/api/v1/TransactionValidation/update/error`, payload, {
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
        });

        if (response.status === 200 && response.data.urlCallback) {
            let redirectUrl = response.data.urlCallback;
            if (!/^https?:\/\//i.test(redirectUrl)) {
                redirectUrl = `https://${redirectUrl}`;
            }             
            window.location.href = redirectUrl;
        } else {
            setErrorMessage("La API no respondió correctamente.");
        }
    } catch (err) {
        console.error("Error al reportar el error a la API:", err);
    }
};


   
    const handleProcessCompletion = async (validationResult) => {
        try {
            const payload = {
                id: transactionId,
                resultJson: JSON.stringify(validationResult),
                transactionIncodeId: session?.interviewId,
                transactionIncodeToken: session?.token
            };

            const response = await axios.put(
                `${apiUrl}/api/v1/TransactionValidation/update`,
                payload,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.status === 200 && response.data.urlCallback) {
                let redirectUrl = response.data.urlCallback;
                if (!/^https?:\/\//i.test(redirectUrl)) {
                    redirectUrl = `https://${redirectUrl}`;
                }                 
                window.location.href = redirectUrl;
            } else {
                setErrorMessage("La API no respondió correctamente.");
            }
        } catch (err) {
            console.error("Error al completar el proceso:", err);
            setError(true);
            setErrorMessage("Error al completar el proceso.");
        }
    };

    if (loading) return <LoadingSpinner />;
    if (isProcessing) return (
        <div className="loading-overlay">
            <div className="loading-content">
                <p>Validación en curso desde el dispositivo móvil...</p>
                <LoadingSpinner text="Procesando, por favor espere..." />
            </div>
        </div>
    );

    if (error) return <ErrorComponent message={errorMessage} />;

    return (
        <div style={{ height: "100vh" }}>
            {showQR ? (
                <div style={containerStyle}>
                    <div style={contentStyle}>
                        <p style={messageStyle}>
                            Escanea el código QR en tu dispositivo móvil para continuar con el proceso de verificación.
                        </p>
                        <QRCodeCanvas value={`${window.location.origin}/IdentityValidationMovil?transactionId=${transactionId}&token=${token}`} size={256} fgColor="#000000" bgColor="#ffffff" level="H" />
                        <p style={messageStyle}>
                            Después de finalizar la validación en el dispositivo móvil, el proceso continuará en este dispositivo.
                        </p>
                    </div>
                </div>
            ) : (
                session ? (
                    <Steps currentStep={step}>
                        <FrontId session={session} onSuccess={goNext}  onError={(e) => handleError("FrontId", { message: e.message, details: e })} />
                        {documentType !== "passport" && (
                            <BackId session={session} onSuccess={goNext} onError={(e) => handleError("BackId", { message: e.message, details: e })} />
                        )}
                        <ProcessId session={session} onSuccess={goNext} onError={(e) => handleError("ProcessId", { message: e.message, details: e })}/>
                        <Selfie
                            session={session}
                            onSuccess={(res) => {
                                setLiveness(res?.liveness);
                                setUserExists(res?.existingUser);
                                goNext();
                            }}
                            onError={(e) => handleError("Selfie", { message: e.message, details: e })}
                        />
                        <FaceMatch
                            session={session}
                            onSuccess={() => {
                                handleProcessCompletion({ liveness, userExists });
                            }}
                            liveness={liveness}
                            userExists={userExists}
                            onError={(e) => handleError("FaceMatch", { message: e.message, details: e })}
                        />
                        <RetrySteps
                            session={session}
                            numberOfTries={3}
                            onSuccess={goNext}                            
                            onError={(e) => handleError("RetrySteps", { message: e.message, details: e })}
                        />
                    </Steps>
                ) : (
                    <LoadingSpinner />
                )
            )}
        </div>
    );
};

// Estilos
const containerStyle = {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
};

const contentStyle = {
    maxWidth: '600px',
    textAlign: 'center',
    padding: '20px',
    border: '1px solid #ccc',
    borderRadius: '8px',
};

const messageStyle = {
    fontSize: '18px',
    marginBottom: '20px',
};

export default IdentityValidation;
